import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Link,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@mui/material'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import Decimal from 'decimal.js'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { PaymentSettingContract } from 'models'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormat } from 'Services/localize/format'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { usePaymentCalculation } from './hook/usePaymentCalculation'
import styles from './paymentDialog.style'
import { QRPromptPayDialog } from './dialogs/QRPreviewDialog'
import Delete from '@mui/icons-material/Delete'
import CustomerSelection from './CustomerSelection'
import { EvoPaymentDialog } from './dialogs/EvoPaymentDialog'
import useSendPaymentToCFD from 'hooks/useSendMessageToCFD/useSendPaymentToCFD'
import { PaymentPayload } from './hook/usePaymentDialog'
import { useConfirmDialog } from 'store/actions/ui.action'
import { mapPaymentTypeName } from 'components/MapPaymentIcon/mapPaymentIcon'

interface PaymentDetailUIProp {
    paymentSettings: PaymentSettingContract[]
    payment: PaymentPayload | undefined
    selectedPaymentType: PaymentSettingContract | undefined
    isReadyToProceedPayment: boolean
    expectAmount: Decimal
    tenderedAmount: Decimal
    orderNumber: string
    onSubmit: () => void
    tipsAmount: number
    clearTips: () => void
    redeemDiscount?: number | undefined
    onClickCustomer?: () => void
    customerID?: string
    resetPaymentStatus: () => void
    onCancelEvoPaymentDialog?: () => void
    disabledUpdateCustomer?: boolean
    hasAwaitingProcessTransaction?: boolean
}

export const ProceedPaymentButton = ({
    onClick,
    isReadyToProceedPayment,
    isMoneyCollect,
    caption,
}: {
    onClick: () => void
    isReadyToProceedPayment: boolean
    isMoneyCollect: boolean
    caption: string
}) => {
    const { isMobile } = useScreenSize()
    return (
        <Box
            paddingY={1}
            sx={isMobile ? {} : { backgroundColor: 'background.default' }}
        >
            <Button
                fullWidth
                color="primary"
                size="large"
                sx={styles.confirmButton}
                onClick={onClick}
                disabled={!isReadyToProceedPayment || !isMoneyCollect}
            >
                {!isReadyToProceedPayment && (
                    <CircularProgress
                        variant="indeterminate"
                        color="primary"
                        size={24}
                        sx={{ mr: 1 }}
                    />
                )}
                <Typography variant="body1">
                    {isReadyToProceedPayment ? caption : 'Processing…'}
                </Typography>
            </Button>
        </Box>
    )
}
const TipsDisplay = ({
    tipsAmount,
    clearTips,
}: {
    tipsAmount: number
    clearTips: () => void
}) => {
    const { t } = useTranslation()
    return (
        <ListItem
            sx={{
                backgroundColor: 'topside.paper',
                display: tipsAmount > 0 ? undefined : 'none',
            }}
        >
            <ListItemText
                primary={
                    <Grid container alignItems={'center'} spacing={1}>
                        <Grid item>
                            <Typography sx={styles.detailTextFont}>
                                {t('finish.label.tips')}: <></>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Link
                                color={'secondary'}
                                onClick={clearTips}
                                sx={{
                                    cursor: 'pointer',
                                }}
                            >
                                <Typography
                                    className="textUpper"
                                    variant={'subtitle2'}
                                >
                                    <Delete
                                        fontSize="small"
                                        sx={{
                                            mb: '-5px',
                                            mr: '3px',
                                        }}
                                    />
                                    {t('finish.button.remove_tip')}
                                </Typography>
                            </Link>
                        </Grid>
                    </Grid>
                }
            />

            <ListItemSecondaryAction
                sx={{
                    backgroundColor: 'topside.paper',
                    display: tipsAmount > 0 ? undefined : 'none',
                }}
            >
                <MonetaryFormat
                    color={'textPrimary'}
                    sx={styles.detailTextFont}
                    value={tipsAmount}
                />
            </ListItemSecondaryAction>
        </ListItem>
    )
}
const CashBalanceDue = ({
    balanceDueAmount,
    cashRoundingAdjustAmount,
}: {
    balanceDueAmount: number
    cashRoundingAdjustAmount: number
}) => {
    const { t } = useTranslation()
    const { currency } = useFormat()
    if (cashRoundingAdjustAmount !== 0)
        return (
            <>
                <ListItem>
                    <ListItemText
                        primary={
                            <Typography sx={styles.detailTextFont}>
                                {t('sell.payment_dialog.balance_due')}{' '}
                            </Typography>
                        }
                    />
                    <ListItemSecondaryAction>
                        <Typography sx={styles.detailTextFont}>
                            {currency(balanceDueAmount)}
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
                <Divider variant="middle" component="li" />
                <ListItem>
                    <ListItemText
                        primary={
                            <Typography sx={styles.detailTextFont}>
                                {t('sell.payment_dialog.rounding_amount')}
                            </Typography>
                        }
                    />
                    <ListItemSecondaryAction>
                        <MonetaryFormat
                            color={
                                cashRoundingAdjustAmount < 0
                                    ? 'error'
                                    : 'textPrimary'
                            }
                            sx={styles.detailTextFont}
                            value={cashRoundingAdjustAmount}
                        />
                    </ListItemSecondaryAction>
                </ListItem>
                <Divider variant="middle" component="li" />
            </>
        )
    return <></>
}

const TenderDetail = ({
    selectedPaymentType,
    collectValue,
    tenderedAmount,
}: {
    selectedPaymentType: PaymentSettingContract | undefined
    collectValue: number
    tenderedAmount: number
}) => {
    const { t } = useTranslation()
    const { currency } = useFormat()

    if (
        selectedPaymentType?.paymentTypeID !== PAYMENT_TYPE_ID.PROMPTPAY &&
        selectedPaymentType?.paymentTypeID !== PAYMENT_TYPE_ID.EVO_PAYMENT
    )
        return (
            <>
                <ListItem>
                    <ListItemText
                        primary={
                            <Typography sx={styles.detailTextFont}>
                                {t('finish.label.collect')}:{' '}
                            </Typography>
                        }
                    />
                    <ListItemSecondaryAction>
                        <Typography sx={styles.detailTextFont}>
                            {currency(collectValue)}
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
                <div>
                    <Divider variant="middle" component="li" />
                    <ListItem>
                        <ListItemText
                            primary={
                                <Typography sx={styles.detailTextFont}>
                                    {`${mapPaymentTypeName(
                                        selectedPaymentType?.paymentTypeID,
                                        true,
                                        {
                                            paymentSetting: selectedPaymentType,
                                        },
                                    )}`}
                                </Typography>
                            }
                        />
                        <ListItemSecondaryAction>
                            <Typography sx={styles.detailTextFont}>
                                {currency(tenderedAmount)}
                            </Typography>
                        </ListItemSecondaryAction>
                    </ListItem>
                </div>
            </>
        )
    return <></>
}
const ChangeDisplay = ({
    selectedPaymentType,
    changeValue,
}: {
    selectedPaymentType: PaymentSettingContract | undefined
    changeValue: number
}) => {
    const { t } = useTranslation()
    if (selectedPaymentType?.paymentTypeID === PAYMENT_TYPE_ID.CASH)
        return (
            <>
                <Divider variant="middle" component="li" />
                <ListItem>
                    <ListItemText
                        primary={
                            <Typography sx={styles.detailTextFont}>
                                {t('finish.label.change')}:{' '}
                            </Typography>
                        }
                    />
                    <ListItemSecondaryAction>
                        <MonetaryFormat
                            color={changeValue!! < 0 ? 'error' : 'textPrimary'}
                            sx={styles.detailTextFont}
                            value={changeValue}
                        />
                    </ListItemSecondaryAction>
                </ListItem>
            </>
        )
    return <></>
}
function PaymentDetailUI({
    paymentSettings,
    payment,
    selectedPaymentType,
    isReadyToProceedPayment,
    expectAmount,
    tenderedAmount,
    orderNumber,
    onSubmit,
    tipsAmount,
    clearTips,
    redeemDiscount,
    onClickCustomer,
    resetPaymentStatus,
    onCancelEvoPaymentDialog,
    customerID,
    disabledUpdateCustomer,
    hasAwaitingProcessTransaction,
}: PaymentDetailUIProp) {
    const { t } = useTranslation()
    const { currency, currencySymbol } = useFormat()
    const { isMediumScreen } = useScreenSize()
    const {
        balanceDueAmount,
        collectValue,
        changeValue,
        currencyCode,
        cashRoundingAdjustAmount,
        isMoneyCollect,
    } = usePaymentCalculation({ selectedPaymentType, expectAmount, payment })

    const { showConfirmDialog } = useConfirmDialog()
    const [openQRPromptpayPreview, setOpenQRPromptpayPreview] = useState(false)
    const [openEvoDialog, setOpenEvoDialog] = useState(false)

    const onEvoDialogOpen = useCallback(async () => {
        const OpenDialog = () => {
            resetPaymentStatus()
            setOpenEvoDialog(true)
        }
        if (hasAwaitingProcessTransaction)
            showConfirmDialog(
                t('sell.payment_dialog.message.pending_integration_left'),
                (res) => {
                    if (res) OpenDialog()
                },
                t('sell.payment_dialog.confirm_title'),
            )
        else OpenDialog()
    }, [
        hasAwaitingProcessTransaction,
        resetPaymentStatus,
        showConfirmDialog,
        t,
    ])

    const onEvoDialogClose = useCallback(() => {
        setOpenEvoDialog(false)
        if (onCancelEvoPaymentDialog) {
            onCancelEvoPaymentDialog()
        }
    }, [onCancelEvoPaymentDialog])

    const onProceedPromptpay = useCallback(async () => {
        setOpenQRPromptpayPreview(true)
    }, [setOpenQRPromptpayPreview])

    const onPaidByPromptpay = useCallback(() => {
        onSubmit()
        setOpenQRPromptpayPreview(false)
    }, [onSubmit, setOpenQRPromptpayPreview])

    const finishButton = useCallback(() => {
        switch (selectedPaymentType?.paymentTypeID) {
            case PAYMENT_TYPE_ID.CASH:
            case PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT:
            case PAYMENT_TYPE_ID.CHANNEL_ACCOUNT:
            case PAYMENT_TYPE_ID.GIFTCARD:
            case PAYMENT_TYPE_ID.CREDITCARD:
                return (
                    <ProceedPaymentButton
                        onClick={onSubmit}
                        isReadyToProceedPayment={isReadyToProceedPayment}
                        isMoneyCollect={isMoneyCollect}
                        caption={t('button.confirm')}
                    />
                )
            case PAYMENT_TYPE_ID.PROMPTPAY:
                return (
                    <ProceedPaymentButton
                        onClick={onProceedPromptpay}
                        isReadyToProceedPayment={isReadyToProceedPayment}
                        isMoneyCollect={isMoneyCollect}
                        caption={t('finish.button.proceed_button')}
                    />
                )
            case PAYMENT_TYPE_ID.EVO_PAYMENT:
                return (
                    <ProceedPaymentButton
                        onClick={onEvoDialogOpen}
                        isReadyToProceedPayment={isReadyToProceedPayment}
                        isMoneyCollect={isMoneyCollect}
                        caption={t('sell.payment_dialog.proceed_payment')}
                    />
                )
            default:
                break
        }
    }, [
        selectedPaymentType,
        onSubmit,
        isReadyToProceedPayment,
        isMoneyCollect,
        t,
        onProceedPromptpay,
        onEvoDialogOpen,
    ])
    const { sendAwaitingPaymentToCFS } = useSendPaymentToCFD()

    useEffect(() => {
        if (payment) sendAwaitingPaymentToCFS(expectAmount.toNumber(), payment)
    }, [expectAmount, payment, sendAwaitingPaymentToCFS])

    return (
        <Grid
            sx={{ ...styles.background, minHeight: 300 }}
            container
            direction="column"
        >
            {!isMediumScreen && (
                <Grid
                    item
                    container
                    justifyContent="space-between"
                    px={'10px'}
                    pt={'10px'}
                >
                    {onClickCustomer && (
                        <Grid item xs pr={1}>
                            <CustomerSelection
                                disabled={disabledUpdateCustomer}
                                onClickCustomer={onClickCustomer}
                                orderCustomerID={customerID}
                            />
                        </Grid>
                    )}
                    <Grid item sx={styles.currencyBannerBG} xs>
                        <Typography align="center" sx={styles.detailTextFont}>
                            {`${currencySymbol} ${currencyCode}`}
                        </Typography>
                    </Grid>
                </Grid>
            )}

            <Grid item xs sx={styles.backgroundTender} mx={1} mt={1}>
                <List dense sx={styles.listDetailContainer}>
                    {isMediumScreen && (
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography sx={styles.detailTextFont}>
                                        {t('finish.label.currency')}
                                    </Typography>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Typography sx={styles.detailTextFont}>
                                    {`${currencySymbol} ${currencyCode}`}
                                </Typography>
                            </ListItemSecondaryAction>
                        </ListItem>
                    )}

                    {redeemDiscount && redeemDiscount !== 0 ? (
                        <>
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography sx={styles.detailTextFont}>
                                            {t(
                                                'sell.payment_dialog.redeem_discount',
                                            )}
                                        </Typography>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <MonetaryFormat
                                        color={'error'}
                                        sx={styles.detailTextFont}
                                        value={redeemDiscount}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                            <Divider variant="middle" component="li" />
                        </>
                    ) : null}

                    <ListItem>
                        <ListItemText
                            primary={
                                <Typography sx={styles.detailTextFont}>
                                    {t('finish.label.total_due')}:{' '}
                                </Typography>
                            }
                        />
                        <ListItemSecondaryAction>
                            <Typography sx={styles.detailTextFont}>
                                {currency(expectAmount.toNumber())}
                            </Typography>
                        </ListItemSecondaryAction>
                    </ListItem>
                    <Divider variant="middle" component="li" />
                    <CashBalanceDue
                        balanceDueAmount={balanceDueAmount.toNumber()}
                        cashRoundingAdjustAmount={cashRoundingAdjustAmount.toNumber()}
                    />
                    <TenderDetail
                        collectValue={collectValue.toNumber()}
                        selectedPaymentType={selectedPaymentType}
                        tenderedAmount={tenderedAmount.toNumber()}
                    />
                </List>
            </Grid>
            <Grid item mx={1} mb={1}>
                <Grid container>
                    <Grid item xs={12}>
                        <List
                            sx={{
                                backgroundColor: 'topside.paper',
                            }}
                        >
                            <TipsDisplay
                                clearTips={clearTips}
                                tipsAmount={tipsAmount}
                            />
                            <ChangeDisplay
                                selectedPaymentType={selectedPaymentType}
                                changeValue={changeValue.toNumber()}
                            />
                        </List>
                    </Grid>
                    <Grid item xs={12}>
                        {finishButton()}
                    </Grid>
                </Grid>
            </Grid>
            {openQRPromptpayPreview && (
                <QRPromptPayDialog
                    onClose={() => setOpenQRPromptpayPreview(false)}
                    amount={expectAmount.toNumber()}
                    openDialog={openQRPromptpayPreview}
                    orderNumber={orderNumber}
                    onPaid={onPaidByPromptpay}
                />
            )}

            {paymentSettings.some(
                (p) => p.paymentTypeID === PAYMENT_TYPE_ID.EVO_PAYMENT,
            ) && (
                <EvoPaymentDialog
                    openDialog={openEvoDialog}
                    onClose={() => onEvoDialogClose()}
                    amount={expectAmount.toNumber()}
                    onPaid={onSubmit}
                />
            )}
        </Grid>
    )
}

export default PaymentDetailUI
